import { useApi } from "./useApi";
import { useActiveGithubOrgForUser } from "./useConnectedGithubOrgs";
import { useOrgFromPathname } from "./useOrgFromPathname";
import type { AxiosError } from "axios";
import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";
import * as z from "zod";
import { workflowJobSchema } from "./useRunDetails";

// 403/404 are expected outcomes when looking up a job (job deleted, log
// retention expired, or the user lacks access to the repo/job), so don't
// retry them. Other failures keep the app-wide default of a single retry.
function retryUnlessExpectedStatus(failureCount: number, error: unknown) {
  const status = (error as AxiosError)?.response?.status;
  if (status === 403 || status === 404) return false;
  return failureCount < 1;
}

// Schema for VM metrics
const vmMetricsPointSchema = z.object({
  timestamp: z.string().transform((date) => new Date(date).getTime()),
  value: z.number(),
});

const metricsSchema = z.object({
  timeseries: z.array(vmMetricsPointSchema),
});

export type Metrics = z.infer<typeof metricsSchema>;

const metricsWithAggregatesSchema = metricsSchema.extend({
  aggregates: z.object({
    sum: z.number().optional(),
    max: z.number().optional(),
    p50: z.number().optional(),
    p90: z.number().optional(),
    p95: z.number().optional(),
    p99: z.number().optional(),
  }),
});

export type MetricsWithAggregates = z.infer<typeof metricsWithAggregatesSchema>;

// Disk metrics are organized by selector (disk device) and type.
// `read`/`written` (and the advanced counters) are optional because the
// backend may omit them for a selector that only saw one direction of I/O.
const diskMetricsSchema = z.record(
  z.string(), // selector key (e.g., 'vda')
  z.object({
    role: z.string().optional(),
    read: metricsWithAggregatesSchema.optional(),
    written: metricsWithAggregatesSchema.optional(),
    reads_completed: metricsWithAggregatesSchema.optional(),
    writes_completed: metricsWithAggregatesSchema.optional(),
    read_bytes: metricsWithAggregatesSchema.optional(),
    written_bytes: metricsWithAggregatesSchema.optional(),
    read_time_ms: metricsWithAggregatesSchema.optional(),
    write_time_ms: metricsWithAggregatesSchema.optional(),
    in_flight: metricsWithAggregatesSchema.optional(),
    io_time_ms: metricsWithAggregatesSchema.optional(),
    weighted_io_time_ms: metricsWithAggregatesSchema.optional(),
  }),
);

// Network metrics are organized by selector (network interface) and type (rx_bytes/tx_bytes)
// Structure: { [selector]: { rx_bytes: { timeseries: [...], aggregates: { sum: number } }, tx_bytes: { timeseries: [...], aggregates: { sum: number } } } }
const networkMetricsSchema = z.record(
  z.string(), // selector key (e.g., 'eth0')
  z.object({
    rx_bytes: metricsWithAggregatesSchema,
    tx_bytes: metricsWithAggregatesSchema,
  }),
);

// TCP retransmission metrics are organized by type (tcp_retrans_segs, tcp_retrans_fast, etc.)
// Structure: { [type]: { timeseries: [...], aggregates: { sum: number } } }
const tcpMetricsSchema = z.record(z.string(), metricsWithAggregatesSchema);

// Conntrack metrics schema - organized by type (count, max, percent, new, destroy, insert, drop, early_drop, insert_failed)
// Structure: { [type]: { timeseries: [...], aggregates: { sum?: number, max?: number, value?: number } } }
const conntrackMetricsEntrySchema = metricsSchema.extend({
  aggregates: z.object({
    sum: z.number().optional(),
    max: z.number().optional(),
    value: z.number().optional(),
  }),
});
const conntrackMetricsSchema = z.record(
  z.string(),
  conntrackMetricsEntrySchema,
);

const oomEventsSchema = z.object({
  events: z.array(
    z.object({
      timestamp: z.string().transform((date) => new Date(date).getTime()),
    }),
  ),
});

export type VMMetricsPoint = z.infer<typeof vmMetricsPointSchema>;

// Memory metrics are organized by type.
// Backend may return empty array when no data is available
const memoryMetricsSchema = z.preprocess(
  (val) => {
    if (Array.isArray(val)) {
      return {
        total_phys: { timeseries: [] },
        used: { timeseries: [] },
      };
    }
    return val;
  },
  z.object({
    total_phys: metricsSchema,
    used: metricsSchema,
    available: metricsSchema.optional(),
    cached: metricsSchema.optional(),
    buffers: metricsSchema.optional(),
    slab_reclaimable: metricsSchema.optional(),
    slab_unreclaimable: metricsSchema.optional(),
    shmem: metricsSchema.optional(),
    page_tables: metricsSchema.optional(),
  }),
);

// Backend may return empty array when no data is available
const diskMetricsSchemaTransformed = z.preprocess((val) => {
  if (Array.isArray(val)) {
    return {};
  }
  return val;
}, diskMetricsSchema);

// Backend may return empty array when no data is available
const networkMetricsSchemaTransformed = z.preprocess((val) => {
  if (Array.isArray(val)) {
    return {};
  }
  return val;
}, networkMetricsSchema);

// Backend may return empty array when no data is available
const tcpMetricsSchemaTransformed = z.preprocess((val) => {
  if (Array.isArray(val)) {
    return {};
  }
  return val;
}, tcpMetricsSchema);

// Backend may return empty array when no data is available
const conntrackMetricsSchemaTransformed = z.preprocess((val) => {
  if (Array.isArray(val)) {
    return {};
  }
  return val;
}, conntrackMetricsSchema);

const vmMetricsSchema = z.preprocess(
  (val) => {
    if (!val || typeof val !== "object" || Array.isArray(val)) {
      return val;
    }
    // Handle arrays for memory, disk, network, tcp, and conntrack fields
    const processed: any = { ...val };
    const valObj = val as Record<string, unknown>;
    if (Array.isArray(valObj.memory)) {
      processed.memory = {
        total_phys: { timeseries: [] },
        used: { timeseries: [] },
      };
    }
    if (Array.isArray(valObj.disk)) {
      processed.disk = {};
    }
    if (Array.isArray(valObj.network)) {
      processed.network = {};
    }
    if (Array.isArray(valObj.tcp)) {
      processed.tcp = {};
    }
    if (Array.isArray(valObj.conntrack)) {
      processed.conntrack = {};
    }
    return processed;
  },
  z.object({
    cpu: metricsWithAggregatesSchema,
    memory: memoryMetricsSchema,
    disk: diskMetricsSchemaTransformed,
    network: networkMetricsSchemaTransformed,
    tcp: z.optional(tcpMetricsSchemaTransformed),
    conntrack: z.optional(conntrackMetricsSchemaTransformed),
    oom: z.optional(oomEventsSchema),
  }),
);

export type VMMetrics = z.infer<typeof vmMetricsSchema>;

// Schema for Docker build
const dockerBuildSchema = z.object({
  id: z.string(),
  arch: z.string(),
  dockerfile_path: z.string(),
  status: z.string(),
  runtime_seconds: z.number(),
  conclusion: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  repo_name: z.string(),
});

export type DockerBuild = z.infer<typeof dockerBuildSchema>;

// Schema for job details response (job info only)
const jobDetailsResponseSchema = z.object({
  job: workflowJobSchema,
});

// Schema for job metrics response
export const jobMetricsResponseSchema = z.object({
  vm_metrics: vmMetricsSchema.nullable(),
});

// Schema for job builds response
const jobBuildsResponseSchema = z.object({
  docker_builds: z.array(dockerBuildSchema).nullable(),
});

const jobLogsResponseSchema = z.array(
  z.object({
    log_timestamp: z.string(),
    log_idx: z.number(),
    log_line_number: z.number(),
    message: z.string(),
    record_name: z.string().nullable(),
    record_order: z
      .number()
      .nullable()
      .transform((val) => val ?? 0),
    record_id: z.string().nullable(),
    level: z.string(),
  }),
);

export type JobDetailsResponse = z.infer<typeof jobDetailsResponseSchema>;
export type JobMetricsResponse = z.infer<typeof jobMetricsResponseSchema>;
export type JobBuildsResponse = z.infer<typeof jobBuildsResponseSchema>;
export type JobLogsResponse = z.infer<typeof jobLogsResponseSchema>;

// Function to fetch job details
async function fetchJobDetails(
  api: ReturnType<typeof useApi>,
  org: string,
  runId: string,
  jobId: string,
): Promise<JobDetailsResponse> {
  const url = `user/github/orgs/${org}/metrics/actions/workflows/runs/${runId}/jobs/${jobId}`;
  const response = await api.get(url);
  return jobDetailsResponseSchema.parse(response.data);
}

// Function to fetch job metrics
async function fetchJobMetrics(
  api: ReturnType<typeof useApi>,
  org: string,
  runId: string,
  jobId: string,
  runnerName?: string | null,
): Promise<JobMetricsResponse> {
  let url = `user/github/orgs/${org}/metrics/actions/workflows/runs/${runId}/jobs/${jobId}/metrics`;
  if (runnerName) {
    url += `?vm_id=${encodeURIComponent(runnerName)}`;
  }
  const response = await api.get(url);
  return jobMetricsResponseSchema.parse(response.data);
}

// Function to fetch job builds
async function fetchJobBuilds(
  api: ReturnType<typeof useApi>,
  org: string,
  runId: string,
  jobId: string,
): Promise<JobBuildsResponse> {
  const url = `user/github/orgs/${org}/metrics/actions/workflows/runs/${runId}/jobs/${jobId}/builds`;
  const response = await api.get(url);
  return jobBuildsResponseSchema.parse(response.data);
}

async function fetchJobLogs(
  api: ReturnType<typeof useApi>,
  org: string,
  jobId: string,
  limit?: number,
  runnerName?: string | null,
): Promise<JobLogsResponse> {
  let url = `user/github/orgs/${org}/metrics/logs/job/stream?job_id=${jobId}`;
  if (limit !== undefined) {
    url += `&limit=${limit}`;
  } else {
    url += `&limit=1000000`; // Default to 1M if no limit specified
  }
  if (runnerName) {
    url += `&vm_id=${encodeURIComponent(runnerName)}`;
  }

  const response = await fetch(`${api.defaults.baseURL}/${url}`, {
    headers: {
      Authorization: String(api.defaults.headers.common.Authorization || ""),
      Accept: "application/x-ndjson",
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();
  const logs: JobLogsResponse = [];

  if (!reader) {
    throw new Error("Response body is not readable");
  }

  try {
    let buffer = ""; // Buffer for incomplete JSON

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      // Split by newlines but keep incomplete lines in buffer
      const lines = buffer.split("\n");
      buffer = lines.pop() || ""; // Keep the last (potentially incomplete) line in buffer

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        try {
          const parsed = JSON.parse(trimmedLine);

          if (parsed.error) {
            throw new Error(parsed.error);
          }

          if (parsed.end) {
            // End of stream marker - we're done
            return jobLogsResponseSchema.parse(logs);
          }

          if (parsed.data && Array.isArray(parsed.data)) {
            logs.push(...parsed.data);
          }
        } catch (parseError) {
          console.warn("Failed to parse streaming log line:", line, parseError);
        }
      }
    }

    return jobLogsResponseSchema.parse(logs);
  } finally {
    reader.releaseLock();
  }
}

// Function to fetch all job logs (using streaming endpoint - no pagination needed)
async function fetchAllJobLogs(
  api: ReturnType<typeof useApi>,
  org: string,
  jobId: string,
  runnerName?: string | null,
): Promise<JobLogsResponse> {
  return await fetchJobLogs(api, org, jobId, 10000, runnerName);
}

// Hook to fetch job details
// When org is provided directly (e.g., from URL), it uses that to start fetching immediately
// without waiting for the useConnectedGithubOrgs suspense to resolve
export function useJobDetails(runId: string, jobId: string, org?: string) {
  const api = useApi();

  // Get org from pathname directly - available immediately unlike router.query
  // which is empty during Next.js hydration
  const urlOrg = useOrgFromPathname();
  const activeGithubOrg = org || urlOrg;

  const detailsQueryKey = useMemo(
    () => ["jobDetails", activeGithubOrg, runId, jobId],
    [activeGithubOrg, runId, jobId],
  );

  // Query for job details
  const {
    data: jobDetails,
    isLoading: isLoadingDetails,
    error: detailsError,
    status: detailsStatus,
  } = useQuery(
    detailsQueryKey,
    async () => {
      if (!activeGithubOrg || !runId || !jobId) {
        return null;
      }
      return await fetchJobDetails(api, activeGithubOrg, runId, jobId);
    },
    {
      enabled: !!activeGithubOrg && !!runId && !!jobId,
      suspense: false,
      retry: retryUnlessExpectedStatus,
      // Poll while the job is still running so steps, runner_name, and conclusion
      // refresh as new webhooks arrive. Stops automatically once the job completes.
      refetchInterval: (data) => {
        if (!data?.job) return false;
        const isRunning =
          data.job.event_status === "in_progress" ||
          data.job.event_status === "queued" ||
          (!data.job.completed_at && !data.job.conclusion);
        return isRunning ? 5000 : false;
      },
    },
  );

  // Extract runner_name from job details for use in query keys
  const runnerName = jobDetails?.job?.runner_name;

  // Whether this job is still in progress. Drives polling for metrics and logs
  // so the dashboard fills in continuously while the job runs.
  const isJobRunning = useMemo(() => {
    const job = jobDetails?.job;
    if (!job) return false;
    return (
      job.event_status === "in_progress" ||
      job.event_status === "queued" ||
      (!job.completed_at && !job.conclusion)
    );
  }, [jobDetails]);

  const metricsQueryKey = useMemo(
    () => ["jobMetrics", activeGithubOrg, runId, jobId, runnerName],
    [activeGithubOrg, runId, jobId, runnerName],
  );

  const buildsQueryKey = useMemo(
    () => ["jobBuilds", activeGithubOrg, runId, jobId],
    [activeGithubOrg, runId, jobId],
  );

  const logsQueryKey = useMemo(
    () => ["jobLogs", activeGithubOrg, jobId, runnerName],
    [activeGithubOrg, jobId, runnerName],
  );

  // Query for job metrics
  const {
    data: jobMetrics,
    isLoading: isLoadingMetrics,
    error: metricsError,
    status: metricsStatus,
  } = useQuery(
    metricsQueryKey,
    async () => {
      if (!activeGithubOrg || !runId || !jobId) {
        return null;
      }
      return await fetchJobMetrics(
        api,
        activeGithubOrg,
        runId,
        jobId,
        runnerName,
      );
    },
    {
      enabled: !!activeGithubOrg && !!runId && !!jobId && !!jobDetails,
      suspense: false,
      retry: retryUnlessExpectedStatus,
      // While the job is in progress, refresh metrics every 10 seconds so CPU,
      // memory, disk, and network charts fill in continuously.
      refetchInterval: isJobRunning ? 10000 : false,
    },
  );

  // Query for job builds
  const {
    data: jobBuilds,
    isLoading: isLoadingBuilds,
    error: buildsError,
  } = useQuery(
    buildsQueryKey,
    async () => {
      if (!activeGithubOrg || !runId || !jobId) {
        return null;
      }
      return await fetchJobBuilds(api, activeGithubOrg, runId, jobId);
    },
    {
      enabled: !!activeGithubOrg && !!runId && !!jobId,
      suspense: false,
      retry: retryUnlessExpectedStatus,
    },
  );

  // Query for job logs
  const {
    data: jobLogs,
    isLoading: isLoadingLogs,
    error: logsError,
  } = useQuery(
    logsQueryKey,
    async () => {
      if (!activeGithubOrg || !jobId) {
        return null;
      }
      return await fetchAllJobLogs(api, activeGithubOrg, jobId);
    },
    {
      enabled: false, // Disabled - now using dedicated streaming hook instead
      suspense: false,
    },
  );

  return {
    data: jobDetails,
    metrics: jobMetrics,
    builds: jobBuilds,
    logs: jobLogs,
    isJobRunning,
    isLoading:
      isLoadingDetails || isLoadingMetrics || isLoadingBuilds || isLoadingLogs,
    error: detailsError || metricsError || buildsError || logsError,
    // Fetch statuses, which unlike loadingStates distinguish a query still gated
    // on job details (idle) from one that has finished loading.
    statuses: {
      details: detailsStatus,
      metrics: metricsStatus,
    },
    // Individual loading states for progressive loading
    loadingStates: {
      details: isLoadingDetails,
      metrics: isLoadingMetrics,
      builds: isLoadingBuilds,
      logs: isLoadingLogs,
    },
    // Individual errors for better error handling
    errors: {
      details: detailsError,
      metrics: metricsError,
      builds: buildsError,
      logs: logsError,
    },
  };
}

// Hook for prefetching job details
export function usePrefetchJobDetails() {
  const queryClient = useQueryClient();
  const api = useApi();
  const { activeGithubOrg } = useActiveGithubOrgForUser();

  const prefetchJobDetails = useCallback(
    async (
      runId: string,
      jobId: string,
      options: { includeMetrics?: boolean } = {},
    ) => {
      if (!activeGithubOrg || !runId || !jobId) return;

      const detailsQueryKey = ["jobDetails", activeGithubOrg, runId, jobId];

      // Only prefetch if the data isn't already in the cache
      const existingDetails = queryClient.getQueryData(detailsQueryKey);

      // Prefetch job details if not cached
      let jobDetailsData = existingDetails as JobDetailsResponse | undefined;
      if (!existingDetails) {
        try {
          jobDetailsData = await queryClient.fetchQuery(
            detailsQueryKey,
            async () => {
              return await fetchJobDetails(api, activeGithubOrg, runId, jobId);
            },
            {
              // Keep the prefetched data for 5 minutes
              staleTime: 5 * 60 * 1000,
              retry: retryUnlessExpectedStatus,
            },
          );
        } catch {
          // Prefetching is best-effort and callers fire-and-forget, so a
          // rejection here would escape as an unhandled promise rejection.
          // Expected 403/404 responses (job deleted, retention expired, or no
          // access) and any other failures are surfaced by useJobDetails when
          // the page actually renders, so just skip the dependent prefetches.
          return;
        }
      }

      const runnerName = jobDetailsData?.job?.runner_name;
      const metricsQueryKey = [
        "jobMetrics",
        activeGithubOrg,
        runId,
        jobId,
        runnerName,
      ];
      const buildsQueryKey = ["jobBuilds", activeGithubOrg, runId, jobId];
      const existingMetrics = queryClient.getQueryData(metricsQueryKey);
      const existingBuilds = queryClient.getQueryData(buildsQueryKey);

      if (options.includeMetrics && !existingMetrics) {
        queryClient.prefetchQuery(
          metricsQueryKey,
          async () => {
            return await fetchJobMetrics(
              api,
              activeGithubOrg,
              runId,
              jobId,
              runnerName,
            );
          },
          {
            staleTime: 5 * 60 * 1000,
          },
        );
      }

      // Prefetch job builds if not cached
      if (!existingBuilds) {
        queryClient.prefetchQuery(
          buildsQueryKey,
          async () => {
            return await fetchJobBuilds(api, activeGithubOrg, runId, jobId);
          },
          {
            staleTime: 5 * 60 * 1000,
          },
        );
      }
    },
    [activeGithubOrg, api, queryClient],
  );

  return prefetchJobDetails;
}

// Schema for step histogram bucket (same shape as job runs histogram)
const stepHistogramBucketSchema = z.object({
  start: z.string(),
  end: z.string(),
  success_count: z.number(),
  failure_count: z.number(),
  cancelled_count: z.number(),
  in_progress_count: z.number().default(0),
  queued_count: z.number().default(0),
  total_count: z.number(),
  avg_duration_seconds: z.number().nullable(),
  runs_with_duration: z.number(),
});

const stepHistogramMetaSchema = z.object({
  total_count: z.number(),
  success_count: z.number(),
  failure_count: z.number(),
  cancelled_count: z.number(),
  in_progress_count: z.number().default(0),
  queued_count: z.number().default(0),
  show_live_statuses: z.boolean().default(false),
});

const stepHistogramResponseSchema = z.object({
  buckets: z.array(stepHistogramBucketSchema),
  meta: stepHistogramMetaSchema,
});

export type StepHistogramBucket = z.infer<typeof stepHistogramBucketSchema>;
export type StepHistogramMeta = z.infer<typeof stepHistogramMetaSchema>;
export type StepHistogramResponse = z.infer<typeof stepHistogramResponseSchema>;

/**
 * Hook for fetching step-level histogram data.
 * Returns time-bucketed run distribution for a specific step
 * within a workflow+job combination over the last 14 days.
 */
export function useStepRunsHistogram({
  workflowName,
  jobName,
  stepName,
  startDate,
  endDate,
}: {
  workflowName: string;
  jobName: string;
  stepName: string | null;
  startDate?: Date;
  endDate?: Date;
}) {
  const api = useApi();
  const { activeGithubOrg } = useActiveGithubOrgForUser();

  const startIso = startDate?.toISOString() ?? "";
  const endIso = endDate?.toISOString() ?? "";

  const queryKey = useMemo(
    () => [
      "stepRunsHistogram",
      activeGithubOrg || "",
      workflowName,
      jobName,
      stepName || "",
      startIso,
      endIso,
    ],
    [activeGithubOrg, workflowName, jobName, stepName, startIso, endIso],
  );

  return useQuery(
    queryKey,
    async () => {
      if (!activeGithubOrg || !stepName) {
        return null;
      }

      const now = endDate ?? new Date();
      const start =
        startDate ?? new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

      const params = new URLSearchParams({
        start_date: start.toISOString(),
        end_date: now.toISOString(),
        step_name: stepName,
        workflow_name: workflowName,
        job_name: jobName,
        bucket_count: "20",
      });

      const endpoint = `user/github/orgs/${activeGithubOrg}/metrics/actions/jobs/runs/step-histogram`;
      const response = await api.get(`${endpoint}?${params.toString()}`);

      return stepHistogramResponseSchema.parse(response.data);
    },
    {
      enabled: !!activeGithubOrg && !!stepName,
      suspense: false,
      staleTime: 5 * 60 * 1000,
      keepPreviousData: true,
    },
  );
}

const stepRunSchema = z.object({
  job_id: z.number(),
  workflow_run_id: z.string(),
  repository_name: z.string(),
  created_at: z.string(),
  status: z.string(),
  duration_seconds: z.number(),
});

const stepRunsResponseSchema = z.object({
  runs: z.array(stepRunSchema),
});

export type StepRun = z.infer<typeof stepRunSchema>;

/**
 * Hook for fetching individual step executions for the step runtime
 * scatter view. The backend samples rows in deterministic hash order so
 * the LIMIT spreads across the whole selected window.
 */
export function useStepRuns({
  workflowName,
  jobName,
  stepName,
  startDate,
  endDate,
  limit,
  enabled = true,
}: {
  workflowName: string;
  jobName: string;
  stepName: string | null;
  startDate?: Date;
  endDate?: Date;
  limit: number;
  enabled?: boolean;
}) {
  const api = useApi();
  const { activeGithubOrg } = useActiveGithubOrgForUser();

  const startIso = startDate?.toISOString() ?? "";
  const endIso = endDate?.toISOString() ?? "";

  const queryKey = useMemo(
    () => [
      "stepRuns",
      activeGithubOrg || "",
      workflowName,
      jobName,
      stepName || "",
      startIso,
      endIso,
      limit,
    ],
    [activeGithubOrg, workflowName, jobName, stepName, startIso, endIso, limit],
  );

  return useQuery(
    queryKey,
    async () => {
      if (!activeGithubOrg || !stepName) {
        return null;
      }

      const now = endDate ?? new Date();
      const start =
        startDate ?? new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

      const params = new URLSearchParams({
        start_date: start.toISOString(),
        end_date: now.toISOString(),
        step_name: stepName,
        workflow_name: workflowName,
        job_name: jobName,
        sample: "1",
        limit: limit.toString(),
      });

      const endpoint = `user/github/orgs/${activeGithubOrg}/metrics/actions/jobs/runs/step-runs`;
      const response = await api.get(`${endpoint}?${params.toString()}`);

      return stepRunsResponseSchema.parse(response.data);
    },
    {
      enabled: enabled && !!activeGithubOrg && !!stepName,
      suspense: false,
      staleTime: 5 * 60 * 1000,
      keepPreviousData: true,
    },
  );
}
